import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, Tray, PaperPlaneTilt, FileText, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DocumentPreviewDrawer from "@/components/DocumentPreviewDrawer";
import IncomingDialog from "./IncomingDialog";
import OwnerLetterDialog from "./OwnerLetterDialog";
import { fmtDateTime } from "./commMeta";

const CATS = ["Schriftverkehr", "Vertrag", "Rechnung", "Protokoll", "Gutachten", "Foto", "Sonstiges"];
const LINK = { tenancy_id: "linked_tenancy_id", property_id: "linked_property_id", vorgang_id: "linked_vorgang_id", contact_id: "linked_contact_id" };

function UploadDialog({ open, onOpenChange, scope, onDone }) {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("Sonstiges");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFiles([]); setTitle(""); } }, [open]);
  const save = async () => {
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("title", files.length === 1 && title ? title : f.name.replace(/\.[^.]+$/, ""));
        fd.append("category", category);
        Object.entries(scope).forEach(([k, v]) => v && LINK[k] && fd.append(LINK[k], v));
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} Dokument${files.length === 1 ? "" : "e"} hochgeladen`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="folder-upload-dialog"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); setFiles((x) => [...x, ...e.dataTransfer.files]); }}>
        <DialogHeader><DialogTitle>Dokument hochladen</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="border-2 border-dashed border-slate-200 rounded-md p-4 text-center text-xs text-slate-500">
            Dateien hierher ziehen oder <label className="underline cursor-pointer">auswählen
              <input type="file" multiple className="hidden" onChange={(e) => setFiles((x) => [...x, ...e.target.files])} data-testid="folder-upload-input" /></label>
            {files.length > 0 && <div className="mt-2 text-slate-700">{files.map((f) => f.name).join(", ")}</div>}
          </div>
          {files.length === 1 && <div><Label>Titel</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={files[0].name} data-testid="folder-upload-title" /></div>}
          <div><Label>Kategorie</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="folder-upload-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button disabled={!files.length || busy} onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="folder-upload-save">{busy ? "Lade hoch…" : "Hochladen"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dokumentenmappe: hochladen, Schriftverkehr/Outlook-Mail einpflegen, Eigentümer informieren
export default function FolderUploads({ scope, ownerTarget }) {
  const [docs, setDocs] = useState([]);
  const [up, setUp] = useState(false);
  const [inc, setInc] = useState(false);
  const [owner, setOwner] = useState(false);
  const [last, setLast] = useState(null);
  const [preview, setPreview] = useState(null);
  const key = JSON.stringify(scope);
  const ownerDocs = useRef([]);
  const load = useCallback(() => {
    const s = JSON.parse(key);
    api.get("/documents", { params: { ...s, limit: 100 } }).then(({ data }) => {
      const rows = (Array.isArray(data) ? data : data.items || []).filter((d) => ["in", "internal"].includes(d.direction) && d.doc_type !== "letter_bundle");
      setDocs(rows);
    }).catch(() => setDocs([]));
  }, [key]);
  useEffect(() => { load(); }, [load]);

  const informOwner = (docIds) => { ownerDocs.current = docIds; setOwner(true); };
  return (
    <div className="space-y-2" data-testid="folder-uploads">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm font-medium text-slate-700 mr-auto">Eingänge & hochgeladene Dokumente</div>
        <Button size="sm" variant="outline" className="gap-1" onClick={() => setUp(true)} data-testid="folder-upload-btn"><UploadSimple size={14} /> Dokument hochladen</Button>
        <Button size="sm" className="gap-1 bg-slate-900 hover:bg-slate-800" onClick={() => setInc(true)} data-testid="folder-incoming-btn"><Tray size={14} /> Schriftverkehr / E-Mail einpflegen</Button>
      </div>
      {last && ownerTarget && (
        <div className="flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="folder-inform-owner-banner">
          „{last.subject}“ gespeichert. Eigentümer über diesen Schriftverkehr informieren?
          <Button size="sm" className="ml-auto h-7 gap-1 text-xs" onClick={() => informOwner([last.document_id, ...(last.attachment_document_ids || [])].filter(Boolean))} data-testid="folder-inform-owner-btn"><PaperPlaneTilt size={12} /> Eigentümer informieren</Button>
          <button type="button" onClick={() => setLast(null)} aria-label="schließen"><X size={12} /></button>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
        {docs.length === 0 && <div className="p-4 text-xs text-slate-500 text-center" data-testid="folder-uploads-empty">Noch keine Eingänge oder hochgeladenen Dokumente.</div>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`folder-doc-${d.id}`}>
            <FileText size={14} className="text-slate-400" />
            <button type="button" className="truncate hover:underline text-left" onClick={() => setPreview(d)}>{d.title || d.original_filename}</button>
            <span className="text-[11px] text-slate-400">{d.category} · {fmtDateTime(d.created_at)}{d.direction === "in" ? " · Eingang" : ""}</span>
            {ownerTarget && <Button size="sm" variant="ghost" className="ml-auto h-6 text-[11px] gap-1" onClick={() => informOwner([d.id])} data-testid={`folder-doc-inform-${d.id}`}><PaperPlaneTilt size={11} /> Eigentümer informieren</Button>}
          </div>
        ))}
      </div>
      <UploadDialog open={up} onOpenChange={setUp} scope={JSON.parse(key)} onDone={load} />
      <IncomingDialog open={inc} onOpenChange={setInc} scope={JSON.parse(key)} onSaved={(e) => { setLast(e); load(); }} />
      {ownerTarget && <OwnerLetterDialog open={owner} onOpenChange={setOwner} target={ownerTarget} attachmentDocIds={ownerDocs.current} />}
      {preview && <DocumentPreviewDrawer docId={preview.id} onClose={() => setPreview(null)} mode="admin" />}
    </div>
  );
}
